import {
  ProductFeatureId,
  type FeaturePermission,
} from '@/actions/permissionActions'

export const FEATURE_CLIENT_DESCRIPTIONS: Partial<
  Record<ProductFeatureId, string>
> = {
  [ProductFeatureId.groupPracticeSupport]:
    'Manage your group practice finances',
  [ProductFeatureId.sCorpEligibilitySupport]:
    'S corp election guidance and support',
  [ProductFeatureId.sCorpComplianceSupport]:
    'S corp compliance with salary and distribution guidance',
  [ProductFeatureId.annualTaxesBusinessFiling]:
    'File your S Corp return (Form 1120-S) with Heard',
  [ProductFeatureId.dedicatedSupport]:
    'A dedicated financial expert who is your primary point of contact all year, four check-ins a year, and unlimited bookable 1:1 meetings (as available)',
  [ProductFeatureId.quarterlyTaxes]:
    'Quarterly tax estimates and payment guidance',
  [ProductFeatureId.managedBookkeeping]: 'Monthly done-for-you bookkeeping',
  [ProductFeatureId.phoneSupport]: 'Scheduled support calls',
  [ProductFeatureId.payrollIncludedForFreeForSelf]:
    'Run payroll for 1 seat at no extra cost',
  [ProductFeatureId.free2ndTaxFiling]: 'Personal tax filing included',
  [ProductFeatureId.payroll]: 'Run payroll for your practice (add on)',
  [ProductFeatureId.annualTaxes]: 'File your taxes with Heard',
}

const isProductFeatureId = (value: string): value is ProductFeatureId =>
  Object.values<string>(ProductFeatureId).includes(value)

const NO_DETAIL_LINES: readonly string[] = []

const UNLIMITED_MEETINGS_LINE =
  'Unlimited 1:1 meetings with your expert from day one, from onboarding to quarterly tax moments (as available).'
const UNLIMITED_MEETINGS_TRIAL_LINE =
  'Unlimited 1:1 meetings with your expert after your trial completes, from onboarding to quarterly tax moments (as available).'
const CHECK_INS_LINE =
  'Four check-ins a year, where your expert reviews your books, sends an agenda, and follows up with a written summary.'
const ALIGNMENT_LINE =
  'Your expert works with your bookkeeper and tax preparer to keep your books, taxes and planning aligned.'

const FEATURE_DETAIL_LINES: Partial<
  Record<ProductFeatureId, readonly string[]>
> = {
  [ProductFeatureId.dedicatedSupport]: [
    UNLIMITED_MEETINGS_LINE,
    CHECK_INS_LINE,
    ALIGNMENT_LINE,
  ],
}

const FEATURE_TRIAL_DETAIL_LINES: Partial<
  Record<ProductFeatureId, readonly string[]>
> = {
  [ProductFeatureId.dedicatedSupport]: [
    UNLIMITED_MEETINGS_TRIAL_LINE,
    CHECK_INS_LINE,
    ALIGNMENT_LINE,
  ],
}

export const featureDetailLinesForId = (
  featureId: string,
  isTrial = false
): readonly string[] => {
  if (!isProductFeatureId(featureId)) return NO_DETAIL_LINES
  const trialLines = isTrial ? FEATURE_TRIAL_DETAIL_LINES[featureId] : undefined
  return trialLines ?? FEATURE_DETAIL_LINES[featureId] ?? NO_DETAIL_LINES
}

/** Sentence case from a snake_case id, so an undescribed feature still reads as English. */
const humanizeFeatureId = (featureId: string): string => {
  const words = featureId.replace(/_/g, ' ').trim()
  if (words.length === 0) return featureId
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * Customer-facing text for a feature id the API returned.
 *
 * Feature ids arrive as plain strings because the vocabulary is API-owned, so this takes a
 * `string` rather than the enum. Anything undescribed — a feature with no entry above, or one
 * this build has never heard of — is humanized rather than dropped: a plan that silently omits
 * a bullet understates what the customer is buying.
 */
export const featureLabelForId = (featureId: string): string => {
  if (isProductFeatureId(featureId)) {
    const described = FEATURE_CLIENT_DESCRIPTIONS[featureId]
    if (described) return described
  }
  return humanizeFeatureId(featureId)
}

/**
 * The copy-only services. The tier map supplies them, so a legacy product's stale grant is
 * kept off every entitlement-driven list rather than doubling them up. Named the same in the
 * API's `entitlement_display`, with no build step comparing the two.
 */
export const UNDISPLAYED_FEATURE_IDS: string[] = [
  ProductFeatureId.sCorpEligibilitySupport,
  ProductFeatureId.sCorpComplianceSupport,
  ProductFeatureId.groupPracticeSupport,
]

const sCorpServiceFor = ({
  tier,
  isLegacy,
  isScorp,
  isGroupPractice,
}: {
  tier: string
  isLegacy: boolean
  isScorp: boolean
  isGroupPractice: boolean
}): string | null => {
  switch (tier) {
    case 'lite':
    case 'basic':
      return ProductFeatureId.sCorpEligibilitySupport
    case 'essential':
    case 'essential_plus':
      return isLegacy && isScorp
        ? ProductFeatureId.sCorpComplianceSupport
        : ProductFeatureId.sCorpEligibilitySupport
    case 'premium':
    case 'premium_plus':
    case 'lite_scorp_group':
      return !isScorp && isGroupPractice
        ? ProductFeatureId.sCorpEligibilitySupport
        : ProductFeatureId.sCorpComplianceSupport
    default:
      return null
  }
}

/**
 * What a tier offers this customer that no entitlement grants. `isLegacy` means the plan is a
 * product outside the current catalog.
 */
export const planTierServicesFor = ({
  tier,
  isLegacy,
  isScorp,
  isGroupPractice,
}: {
  tier: string | null | undefined
  isLegacy: boolean
  isScorp: boolean
  isGroupPractice: boolean
}): string[] => {
  if (!tier) return []
  const sCorpService = sCorpServiceFor({
    tier,
    isLegacy,
    isScorp,
    isGroupPractice,
  })
  return [
    ...(sCorpService ? [sCorpService] : []),
    ...((tier === 'premium' ||
      tier === 'premium_plus' ||
      tier === 'lite_scorp_group') &&
    isGroupPractice
      ? [ProductFeatureId.groupPracticeSupport]
      : []),
  ]
}

/** The Plan Services list: what the plan grants, then what its tier includes. */
export const planServiceLabels = ({
  featurePermissions,
  planTierServices,
}: {
  featurePermissions: FeaturePermission[]
  planTierServices: string[]
}): string[] => [
  ...featurePermissions
    .filter((fp) => !UNDISPLAYED_FEATURE_IDS.includes(fp.id))
    .map((fp) => FEATURE_CLIENT_DESCRIPTIONS[fp.id] ?? fp.name),
  ...planTierServices.map(featureLabelForId),
]
